import {
  useCallback,
  useEffect,
  useRef,
  useState,
  type ClipboardEvent,
  type KeyboardEvent,
} from 'react';
import {
  BookOpenIcon,
  GifIcon,
  PhotoIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { PaperAirplaneIcon } from '@heroicons/react/24/solid';
import type {
  GroupMember,
  Mention,
  Message,
  MessageContent,
  ScriptureReference,
  SendMessageData,
} from '@flock-types/chat.types';
import useMediaUpload from '@/hooks/useMediaUpload';
import type { GiphyGif } from '@/services/giphy';
import MentionAutocomplete from './MentionAutocomplete';
import GifPickerPanel from './GifPickerPanel';
import ScripturePickerPanel from './ScripturePickerPanel';

const MAX_LENGTH = 4000;

export interface ComposerEditState {
  message: Message;
}

export interface ComposerReplyState {
  message: Message;
}

interface SelectedImage {
  file: File;
  previewUrl: string;
}

interface Props {
  groupId: string;
  onSend: (data: SendMessageData) => Promise<void>;
  onEditSubmit?: (messageId: string, newText: string) => Promise<void>;
  members: GroupMember[];
  disabled?: boolean;
  placeholder?: string;
  replyTo: ComposerReplyState | null;
  onCancelReply: () => void;
  editState: ComposerEditState | null;
  onCancelEdit: () => void;
  onEditLastRequested?: () => void;
  onTyping?: (isTyping: boolean) => void;
  /** Files dropped onto the conversation pane (drag & drop). */
  incomingFiles?: File[] | null;
  onIncomingFilesConsumed?: () => void;
}

export default function MessageComposer({
  groupId,
  onSend,
  onEditSubmit,
  members,
  disabled,
  placeholder = 'Type a message',
  replyTo,
  onCancelReply,
  editState,
  onCancelEdit,
  onEditLastRequested,
  onTyping,
  incomingFiles,
  onIncomingFilesConsumed,
}: Props) {
  const [text, setText] = useState('');
  const [isSending, setIsSending] = useState(false);
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionStartIndex, setMentionStartIndex] = useState<number | null>(null);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionSelectedIndex, setMentionSelectedIndex] = useState(0);
  const [selectedImage, setSelectedImage] = useState<SelectedImage | null>(null);
  const [selectedScripture, setSelectedScripture] = useState<ScriptureReference | null>(null);
  const [activePanel, setActivePanel] = useState<'gif' | 'scripture' | null>(null);
  const [sendError, setSendError] = useState<string | null>(null);
  const filteredMembersRef = useRef<GroupMember[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const { uploadImage, progress, cancelUpload, isUploading } = useMediaUpload({ groupId });

  // Seed text when entering edit mode
  useEffect(() => {
    if (editState) {
      setText(editState.message.content?.text ?? '');
      setMentions(editState.message.mentions ?? []);
      setSelectedImage(null);
      setSelectedScripture(null);
      textareaRef.current?.focus();
    } else {
      setText('');
      setMentions([]);
    }
    setShowMentions(false);
    setMentionStartIndex(null);
  }, [editState]);

  useEffect(() => {
    if (replyTo) textareaRef.current?.focus();
  }, [replyTo]);

  // Revoke object URLs on change/unmount
  useEffect(() => {
    return () => {
      if (selectedImage) URL.revokeObjectURL(selectedImage.previewUrl);
    };
  }, [selectedImage]);

  const autosize = useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 160)}px`;
  }, []);
  useEffect(autosize, [text, autosize]);

  const detectMentionTrigger = useCallback((value: string, cursorPosition: number) => {
    const textBeforeCursor = value.slice(0, cursorPosition);
    const atIndex = textBeforeCursor.lastIndexOf('@');
    if (atIndex === -1) {
      setShowMentions(false);
      setMentionStartIndex(null);
      return;
    }
    const charBeforeAt = atIndex > 0 ? textBeforeCursor[atIndex - 1] : ' ';
    if (!/\s/.test(charBeforeAt) && atIndex !== 0) {
      setShowMentions(false);
      setMentionStartIndex(null);
      return;
    }
    const query = textBeforeCursor.slice(atIndex + 1);
    if (/\s/.test(query)) {
      setShowMentions(false);
      setMentionStartIndex(null);
      return;
    }
    setShowMentions(true);
    setMentionStartIndex(atIndex);
    setMentionQuery(query);
    setMentionSelectedIndex(0);
  }, []);

  const handleChange = useCallback(
    (value: string) => {
      setText(value.slice(0, MAX_LENGTH));
      const caret = textareaRef.current?.selectionStart ?? value.length;
      detectMentionTrigger(value, caret);
      if (value.trim()) onTyping?.(true);
    },
    [detectMentionTrigger, onTyping]
  );

  const handleMentionSelect = useCallback(
    (member: GroupMember) => {
      if (mentionStartIndex === null) return;
      const caret = textareaRef.current?.selectionStart ?? text.length;
      const beforeMention = text.slice(0, mentionStartIndex);
      const afterCaret = text.slice(caret);
      const mentionText = `@${member.userName} `;
      const newText = beforeMention + mentionText + afterCaret;
      setText(newText);

      setMentions((prev) => {
        if (prev.some((m) => m.userId === member.userId)) return prev;
        return [
          ...prev,
          {
            userId: member.userId,
            userName: member.userName,
            startIndex: mentionStartIndex,
            endIndex: mentionStartIndex + mentionText.length - 1,
          },
        ];
      });

      setShowMentions(false);
      setMentionStartIndex(null);
      requestAnimationFrame(() => {
        const el = textareaRef.current;
        if (el) {
          el.focus();
          const pos = mentionStartIndex + mentionText.length;
          el.setSelectionRange(pos, pos);
        }
      });
    },
    [mentionStartIndex, text]
  );

  /** Recompute mention offsets against the final text; drop missing ones. */
  const finalizeMentions = useCallback((finalText: string, source: Mention[]): Mention[] => {
    const out: Mention[] = [];
    let searchFrom = 0;
    const ordered = [...source].sort((a, b) => a.startIndex - b.startIndex);
    for (const m of ordered) {
      const token = `@${m.userName}`;
      let idx = finalText.indexOf(token, searchFrom);
      if (idx === -1) idx = finalText.indexOf(token);
      if (idx === -1) continue;
      out.push({ ...m, startIndex: idx, endIndex: idx + token.length });
      searchFrom = idx + token.length;
    }
    return out;
  }, []);

  const acceptFiles = useCallback((files: File[]) => {
    const image = files.find((f) => f.type.startsWith('image/') || /\.(heic|heif)$/i.test(f.name));
    if (!image) return;
    setSendError(null);
    setSelectedImage((prev) => {
      if (prev) URL.revokeObjectURL(prev.previewUrl);
      return { file: image, previewUrl: URL.createObjectURL(image) };
    });
    textareaRef.current?.focus();
  }, []);

  // Consume files dropped on the conversation pane
  useEffect(() => {
    if (incomingFiles && incomingFiles.length > 0) {
      acceptFiles(incomingFiles);
      onIncomingFilesConsumed?.();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [incomingFiles, acceptFiles]);

  const buildReplyTo = useCallback((): SendMessageData['replyTo'] => {
    if (!replyTo) return undefined;
    return {
      messageId: replyTo.message.id,
      text: replyTo.message.content?.text?.slice(0, 100) || '[Media]',
      userName: replyTo.message.userName,
    };
  }, [replyTo]);

  /** Send GIF immediately on pick (mobile parity). */
  const handleGifSelect = useCallback(
    async (gif: GiphyGif) => {
      if (disabled || isSending) return;
      setActivePanel(null);
      setIsSending(true);
      try {
        const fixedHeight = gif.images.fixed_height;
        await onSend({
          type: 'gif',
          content: {
            mediaUrl: fixedHeight.url,
            thumbnailUrl: gif.images.fixed_height_still.url,
            imageWidth: parseInt(fixedHeight.width, 10),
            imageHeight: parseInt(fixedHeight.height, 10),
            giphyId: gif.id,
            giphyOriginalUrl: gif.images.original.url,
          },
          replyTo: buildReplyTo(),
        });
        onCancelReply();
      } catch (err) {
        console.error('[Composer] GIF send failed:', err);
        setSendError('Failed to send GIF. Please try again.');
      } finally {
        setIsSending(false);
      }
    },
    [disabled, isSending, onSend, buildReplyTo, onCancelReply]
  );

  /** Send a scripture card immediately (mobile "Send Now"). */
  const handleScriptureSendNow = useCallback(
    async (scripture: ScriptureReference) => {
      if (disabled || isSending) return;
      setActivePanel(null);
      setIsSending(true);
      try {
        await onSend({
          type: 'text',
          content: {
            text: `“${scripture.text}” — ${scripture.reference} (${scripture.translation})`,
            scripture,
          },
          replyTo: buildReplyTo(),
        });
        onCancelReply();
      } catch (err) {
        console.error('[Composer] Scripture send failed:', err);
        setSendError('Failed to send scripture. Please try again.');
      } finally {
        setIsSending(false);
      }
    },
    [disabled, isSending, onSend, buildReplyTo, onCancelReply]
  );

  const handleSubmit = useCallback(async () => {
    const trimmed = text.trim();
    const hasText = trimmed.length > 0;
    const hasImage = selectedImage !== null;
    const hasScripture = selectedScripture !== null;
    if ((!hasText && !hasImage && !hasScripture) || isSending || disabled || isUploading) return;

    if (editState && onEditSubmit) {
      if (!hasText) return;
      setIsSending(true);
      try {
        await onEditSubmit(editState.message.id, trimmed);
        onCancelEdit();
        setText('');
        setMentions([]);
      } catch (e) {
        console.warn('[Composer] Edit failed:', e);
        setSendError('Failed to save edit.');
      } finally {
        setIsSending(false);
      }
      return;
    }

    setIsSending(true);
    setSendError(null);
    onTyping?.(false);
    try {
      // Mobile-parity content assembly (flock-expo MessageComposer handleSend)
      let content: MessageContent = {};
      let messageType: SendMessageData['type'] = 'text';

      if (hasImage && selectedImage) {
        const uploadResult = await uploadImage(selectedImage.file);
        content = {
          mediaUrl: uploadResult.url,
          mediaType: uploadResult.mimeType,
          fileName: uploadResult.fileName,
          fileSize: uploadResult.fileSize,
          imageWidth: uploadResult.imageWidth,
          imageHeight: uploadResult.imageHeight,
        };
        messageType = 'image';
        if (hasText) content.text = trimmed;
      } else if (hasScripture && selectedScripture) {
        content.scripture = selectedScripture;
        const verseFallback = `“${selectedScripture.text}” — ${selectedScripture.reference} (${selectedScripture.translation})`;
        if (hasText) {
          content.userText = trimmed;
          content.text = `${trimmed}\n\n${verseFallback}`;
        } else {
          content.text = verseFallback;
        }
      } else {
        content.text = trimmed;
      }

      const finalMentions = hasText ? finalizeMentions(trimmed, mentions) : [];
      await onSend({
        type: messageType,
        content,
        mentions: finalMentions.length > 0 ? finalMentions : undefined,
        replyTo: buildReplyTo(),
      });

      setText('');
      setMentions([]);
      setSelectedImage((prev) => {
        if (prev) URL.revokeObjectURL(prev.previewUrl);
        return null;
      });
      setSelectedScripture(null);
      onCancelReply();
    } catch (e) {
      console.warn('[Composer] Send failed:', e);
      setSendError('Failed to send. Please try again.');
    } finally {
      setIsSending(false);
      textareaRef.current?.focus();
    }
  }, [
    text,
    selectedImage,
    selectedScripture,
    isSending,
    disabled,
    isUploading,
    editState,
    onEditSubmit,
    onCancelEdit,
    onTyping,
    uploadImage,
    finalizeMentions,
    mentions,
    onSend,
    buildReplyTo,
    onCancelReply,
  ]);

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLTextAreaElement>) => {
      if (showMentions && filteredMembersRef.current.length > 0) {
        if (e.key === 'ArrowDown') {
          e.preventDefault();
          setMentionSelectedIndex((i) => Math.min(i + 1, filteredMembersRef.current.length - 1));
          return;
        }
        if (e.key === 'ArrowUp') {
          e.preventDefault();
          setMentionSelectedIndex((i) => Math.max(i - 1, 0));
          return;
        }
        if (e.key === 'Enter' || e.key === 'Tab') {
          e.preventDefault();
          const member = filteredMembersRef.current[mentionSelectedIndex];
          if (member) handleMentionSelect(member);
          return;
        }
        if (e.key === 'Escape') {
          e.preventDefault();
          setShowMentions(false);
          setMentionStartIndex(null);
          return;
        }
      }

      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSubmit();
        return;
      }
      if (e.key === 'Escape') {
        if (editState) onCancelEdit();
        else if (replyTo) onCancelReply();
        else if (activePanel) setActivePanel(null);
        return;
      }
      if (e.key === 'ArrowUp' && text === '' && !editState && onEditLastRequested) {
        e.preventDefault();
        onEditLastRequested();
      }
    },
    [
      showMentions,
      mentionSelectedIndex,
      handleMentionSelect,
      handleSubmit,
      editState,
      onCancelEdit,
      replyTo,
      onCancelReply,
      activePanel,
      text,
      onEditLastRequested,
    ]
  );

  const handlePaste = useCallback(
    (e: ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.clipboardData?.files ?? []);
      if (files.length > 0) {
        e.preventDefault();
        acceptFiles(files);
      }
    },
    [acceptFiles]
  );

  const canSend =
    !disabled &&
    !isSending &&
    !isUploading &&
    (text.trim().length > 0 || selectedImage !== null || selectedScripture !== null);

  return (
    <div className="border-t border-flock-gray-200 bg-white px-4 py-3">
      {sendError && (
        <div className="mb-2 rounded-lg border border-red-200 bg-red-50 px-3 py-1.5 text-xs text-red-700">
          {sendError}
        </div>
      )}

      {replyTo && !editState && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border-l-2 border-flock-navy-400 bg-flock-navy-50 px-3 py-1.5">
          <div className="min-w-0 flex-1 text-xs">
            <span className="font-semibold text-flock-navy-700">
              Replying to {replyTo.message.userName}
            </span>
            <span className="block truncate text-flock-navy-500">
              {replyTo.message.content?.text || 'Message'}
            </span>
          </div>
          <button onClick={onCancelReply} className="p-1 text-flock-navy-400 hover:text-flock-navy-600">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {editState && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border-l-2 border-amber-400 bg-amber-50 px-3 py-1.5">
          <span className="flex-1 text-xs font-semibold text-amber-700">
            Editing message — Esc to cancel
          </span>
          <button onClick={onCancelEdit} className="p-1 text-amber-500 hover:text-amber-700">
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {selectedImage && (
        <div className="mb-2 flex items-center gap-3 rounded-lg border border-flock-gray-200 bg-flock-navy-50 p-2">
          <img src={selectedImage.previewUrl} alt="" className="h-14 w-14 rounded-lg object-cover" />
          <div className="min-w-0 flex-1 text-xs text-flock-navy-600">
            <div className="truncate font-medium">{selectedImage.file.name}</div>
            {isUploading && progress ? (
              <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-flock-gray-200">
                <div
                  className="h-full bg-flock-navy-600 transition-all"
                  style={{ width: `${progress.progress}%` }}
                />
              </div>
            ) : (
              <span className="text-flock-navy-400">Ready to send</span>
            )}
          </div>
          <button
            onClick={() => {
              if (isUploading) cancelUpload();
              setSelectedImage((prev) => {
                if (prev) URL.revokeObjectURL(prev.previewUrl);
                return null;
              });
            }}
            className="p-1 text-flock-navy-400 hover:text-flock-navy-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      {selectedScripture && (
        <div className="mb-2 flex items-center gap-2 rounded-lg border-l-2 border-flock-navy-400 bg-flock-navy-50 px-3 py-1.5">
          <div className="min-w-0 flex-1 text-xs">
            <span className="font-semibold text-flock-navy-700">
              {selectedScripture.reference} ({selectedScripture.translation})
            </span>
            <span className="block truncate italic text-flock-navy-500">
              “{selectedScripture.text}”
            </span>
          </div>
          <button
            onClick={() => setSelectedScripture(null)}
            className="p-1 text-flock-navy-400 hover:text-flock-navy-600"
          >
            <XMarkIcon className="h-4 w-4" />
          </button>
        </div>
      )}

      <div className="relative flex items-end gap-1.5">
        {showMentions && (
          <MentionAutocomplete
            members={members}
            searchQuery={mentionQuery}
            selectedIndex={mentionSelectedIndex}
            onSelect={handleMentionSelect}
            onSelectedIndexChange={setMentionSelectedIndex}
            onFilteredChange={(f) => {
              filteredMembersRef.current = f;
            }}
          />
        )}

        {activePanel === 'gif' && (
          <GifPickerPanel onSelect={handleGifSelect} onClose={() => setActivePanel(null)} />
        )}
        {activePanel === 'scripture' && (
          <ScripturePickerPanel
            onSendNow={handleScriptureSendNow}
            onInsert={(s) => {
              setSelectedScripture(s);
              setActivePanel(null);
              textareaRef.current?.focus();
            }}
            onClose={() => setActivePanel(null)}
          />
        )}

        {!editState && (
          <div className="flex items-center gap-0.5 pb-1">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*,.heic,.heif"
              className="hidden"
              onChange={(e) => {
                const files = Array.from(e.target.files ?? []);
                if (files.length) acceptFiles(files);
                e.target.value = '';
              }}
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={disabled}
              title="Add a photo"
              className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50 hover:text-flock-navy-600"
            >
              <PhotoIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setActivePanel((p) => (p === 'gif' ? null : 'gif'))}
              disabled={disabled}
              title="Send a GIF"
              className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50 hover:text-flock-navy-600"
            >
              <GifIcon className="h-5 w-5" />
            </button>
            <button
              onClick={() => setActivePanel((p) => (p === 'scripture' ? null : 'scripture'))}
              disabled={disabled}
              title="Share scripture"
              className="rounded-lg p-2 text-flock-navy-400 transition hover:bg-flock-navy-50 hover:text-flock-navy-600"
            >
              <BookOpenIcon className="h-5 w-5" />
            </button>
          </div>
        )}

        <textarea
          ref={textareaRef}
          value={text}
          onChange={(e) => handleChange(e.target.value)}
          onKeyDown={handleKeyDown}
          onPaste={handlePaste}
          onClick={() => {
            const caret = textareaRef.current?.selectionStart ?? text.length;
            detectMentionTrigger(text, caret);
          }}
          rows={1}
          disabled={disabled}
          placeholder={selectedImage ? 'Add a caption (optional)' : placeholder}
          className="max-h-40 min-h-[42px] flex-1 resize-none rounded-xl border border-flock-gray-200 bg-flock-navy-50 px-4 py-2.5 text-sm text-flock-navy-900 outline-none placeholder:text-flock-navy-300 focus:border-flock-navy-400 disabled:opacity-60"
        />

        <button
          onClick={handleSubmit}
          disabled={!canSend}
          title={editState ? 'Save edit' : 'Send'}
          className="min-h-touch min-w-touch flex items-center justify-center rounded-xl bg-flock-navy-600 p-2.5 text-white transition hover:bg-flock-navy-700 disabled:opacity-40"
        >
          <PaperAirplaneIcon className="h-5 w-5" />
        </button>
      </div>
    </div>
  );
}
